import PropTypes from 'prop-types';

import {Box, Skeleton, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import Breadcrumbs from '@amblique/king-living/app/components/breadcrumbs';
import {resolveContentfulLinkTargetProps} from '@amblique/king-living/app/contentful/helpers/resolve-contentful-link-url';

/**
 * Normalize jsonData to breadcrumb items array
 * Handles multiple formats:
 * - Contentful format: { items: [{ name, path? }] }
 * - Direct array: [{ name, path? }]
 * - JSON string: "{ \"items\": [...] }"
 *
 * @param {Object|string|Array} content - JSON data from Contentful
 * @returns {Array} Array of breadcrumb items
 */
const normalizeContent = (content) => {
  if (!content) return [];

  let value = content;

  // Parse JSON string if needed
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch (error) {
      return [];
    }
  }

  // Handle different data structures
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.breadcrumbs)) return value.breadcrumbs;

  return [];
};

/**
 * Normalize breadcrumb item to standard format
 * Supports multiple formats:
 * - Contentful format: { name: string, path?: string }
 * - Generic format: { label|title|text|name: string, href|url|path?: string }
 * - String: plain text label
 *
 * @param {Object|string} item - Breadcrumb item data
 * @param {number} index - Item index for fallback ID
 * @returns {Object|null} Normalized breadcrumb item { id, label, href? }
 */
const normalizeBreadcrumbItem = (item, index) => {
  if (!item) return null;

  // Handle string items
  if (typeof item === 'string') {
    return {id: `breadcrumb-${index}`, label: item};
  }

  // Extract label - prioritize 'name' for Contentful format, then fallback to other fields
  const rawLabel = item.name || item.label || item.title || item.text;

  if (!rawLabel) return null;

  // Extract href/path - prioritize 'path' for Contentful format, then fallback to other fields
  const rawHref = item.path || item.href || item.url;

  const href = resolveContentfulLinkTargetProps(rawHref).linkUrl || undefined;

  return {
    id: item.id || item.sys?.id || `breadcrumb-${index}`,
    label: rawLabel,
    href, // href is optional - last item may not have a link
  };
};

/**
 * @override
 * Breadcrumb component from Contentful
 *
 * Expects jsonData field with format:
 * {
 *   "items": [
 *     { "name": "Home", "path": "/" },
 *     { "name": "Discover" },
 *     { "name": "Current Page" }
 *   ]
 * }
 *
 * @param {Object} props - Component props
 * @param {Contentful.Breadcrumb} props.component - Breadcrumb component data
 * @param {Object} props.component.jsonData - JSON data with items array
 * @param {Array<{name: string, path?: string}>} props.component.jsonData.items - Breadcrumb items
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement|null}
 */
export const Breadcrumb = ({component, isComplete = true, isFetching = false, ...props}) => {
  const styles = useMultiStyleConfig('contentful/breadcrumb', {component, isComplete, isFetching});

  if (!component) return null;

  if (!isComplete && isFetching) {
    return (
      <Box {...styles.root} {...props}>
        <Skeleton height="20px" width="100%" />
      </Box>
    );
  }

  const items = normalizeContent(component?.jsonData)
    .map(normalizeBreadcrumbItem)
    .filter(Boolean);

  if (!items.length) return null;

  return (
    <Box className="contentful-breadcrumb" {...styles.root} {...props}>
      <Box className="breadcrumb-container" {...styles.container}>
        <Breadcrumbs breadcrumbs={items} />
      </Box>
    </Box>
  );
};

Breadcrumb.displayName = 'Breadcrumb';
Breadcrumb.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
